import type { ComponentType, SVGProps } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  DocumentTextIcon,
  BanknotesIcon,
  ScaleIcon,
  Cog6ToothIcon,
  BuildingStorefrontIcon,
  ChartBarIcon,
  HeartIcon,
  UserGroupIcon,
  CreditCardIcon,
  GiftIcon,
  CubeIcon,
  ShoppingCartIcon,
  TableCellsIcon,
  MagnifyingGlassIcon,
  CloudIcon,
  PresentationChartLineIcon,
  BuildingOffice2Icon,
  BuildingLibraryIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline'
import {
  NavSidebar,
  NavSection,
  NavItem,
  AlertCountBadge,
} from '@cottage/design-system/internal-tooling/ui'
import { navPagesForSection, type NavSection as NavPageSection } from '@/config/navPages'
import { usePendingReviewStore } from '@/store/pendingReviewStore'
import { usePurchasingNavStore } from '@/store/purchasingNavStore'
import { useMonthCloseNavStore } from '@/store/monthCloseNavStore'
import { useCloverNavStore } from '@/store/cloverNavStore'
import {
  cloverSidebarAlertCount,
  cloverSidebarAlertLabel,
} from '@/pages/clover/cloverNavAlerts'
import { useWarehouseProblemStore } from '@/store/warehouseProblemStore'
import { useFixedAssetsNavStore } from '@/store/fixedAssetsNavStore'
import { useAuth } from '@/contexts/AuthContext'
import { canView, type PageKey } from '@/utils/pagePermissions'
import type { AuthUser } from '@/services/auth'

interface SidebarProps {
  collapsed?: boolean
  onToggleCollapse?: () => void
  mobileOpen?: boolean
  onMobileClose?: () => void
}

type NavItemConfig = {
  to: string
  label: string
  icon: ComponentType<SVGProps<SVGSVGElement>>
  pageKey: PageKey
  isActive: (pathname: string) => boolean
}

const NAV_ITEM_META: Record<
  PageKey,
  Pick<NavItemConfig, 'icon' | 'isActive'>
> = {
  invoices: {
    icon: DocumentTextIcon,
    isActive: (pathname) =>
      pathname.startsWith('/bookkeeper') || pathname.startsWith('/invoice-minion'),
  },
  'member-ledger': {
    icon: TableCellsIcon,
    isActive: (pathname) => pathname.startsWith('/member-ledger'),
  },
  transactions: {
    icon: MagnifyingGlassIcon,
    isActive: (pathname) => pathname.startsWith('/qbo-transaction-search'),
  },
  vendors: {
    icon: BuildingStorefrontIcon,
    isActive: (pathname) => pathname.startsWith('/vendor-details'),
  },
  'hp-threshold': {
    icon: ScaleIcon,
    isActive: (pathname) => pathname.startsWith('/hp-threshold'),
  },
  projections: {
    icon: ArrowTrendingUpIcon,
    isActive: (pathname) => pathname.startsWith('/projections'),
  },
  gcp: {
    icon: CloudIcon,
    isActive: (pathname) => pathname.startsWith('/gcp'),
  },
  'monthly-performance': {
    icon: PresentationChartLineIcon,
    isActive: (pathname) => pathname.startsWith('/reporting'),
  },
  'clover-reports': {
    icon: CreditCardIcon,
    isActive: (pathname) => pathname.startsWith('/clover'),
  },
  'bank-recon': {
    icon: BuildingLibraryIcon,
    isActive: (pathname) => pathname.startsWith('/bank-recon'),
  },
  warehouse: {
    icon: CubeIcon,
    isActive: (pathname) => pathname.startsWith('/warehouse'),
  },
  purchasing: {
    icon: ShoppingCartIcon,
    isActive: (pathname) => pathname.startsWith('/purchasing'),
  },
  'gift-cards': {
    icon: GiftIcon,
    isActive: (pathname) => pathname.startsWith('/gift-cards'),
  },
  payroll: {
    icon: UserGroupIcon,
    isActive: (pathname) => pathname.startsWith('/payroll'),
  },
  benefits: {
    icon: HeartIcon,
    isActive: (pathname) => pathname.startsWith('/benefits'),
  },
  'fixed-assets': {
    icon: BuildingOffice2Icon,
    isActive: (pathname) => pathname.startsWith('/fixed-assets'),
  },
  'preferred-equity': {
    icon: ChartBarIcon,
    isActive: (pathname) => pathname.startsWith('/preferred-equity'),
  },
  distributions: {
    icon: BanknotesIcon,
    isActive: (pathname) =>
      pathname.startsWith('/distribution-waterfall'),
  },
  settings: {
    icon: Cog6ToothIcon,
    isActive: (pathname) => pathname === '/settings',
  },
}

function buildNavItems(section: Exclude<NavPageSection, 'Settings'>): NavItemConfig[] {
  return navPagesForSection(section).map((page) => {
    const meta = NAV_ITEM_META[page.pageKey]
    return {
      to: page.defaultPath,
      label: page.label,
      icon: meta.icon,
      pageKey: page.pageKey,
      isActive: meta.isActive,
    }
  })
}

const payablesNavItems = buildNavItems('Payables')
const booksNavItems = buildNavItems('Books')
const membersNavItems = buildNavItems('Members')
const financialsNavItems = buildNavItems('Financials')

function filterVisibleNavItems(items: NavItemConfig[], user: AuthUser | null) {
  return items.filter((item) => canView(user, item.pageKey))
}

type NavItemAlert = {
  count: number
  label: string
  tone?: 'error' | 'warning'
}

function SidebarNavItem({
  item,
  onClose,
  alert,
}: {
  item: NavItemConfig
  onClose: () => void
  alert?: NavItemAlert | null
}) {
  const location = useLocation()
  const navigate = useNavigate()
  const active = item.isActive(location.pathname)
  const Icon = item.icon
  const hasAlert = alert != null && alert.count > 0

  return (
    <NavItem
      icon={<Icon className="h-5 w-5" />}
      title={item.label}
      label={<span className="block truncate">{item.label}</span>}
      active={active}
      badge={
        hasAlert ? (
          <AlertCountBadge
            count={alert.count}
            label={alert.label}
            variant="nav"
            tone={alert.tone ?? 'error'}
            active={active}
          />
        ) : undefined
      }
      onClick={() => {
        navigate(item.to)
        onClose()
      }}
    />
  )
}

function SidebarNavSection({
  label,
  items,
  onClose,
  getItemAlert,
}: {
  label: string
  items: NavItemConfig[]
  onClose: () => void
  getItemAlert?: (item: NavItemConfig) => NavItemAlert | null
}) {
  if (items.length === 0) {
    return null
  }

  return (
    <NavSection label={label}>
      {items.map((item) => (
        <SidebarNavItem
          key={item.to}
          item={item}
          onClose={onClose}
          alert={getItemAlert?.(item) ?? null}
        />
      ))}
    </NavSection>
  )
}

export default function Sidebar({
  collapsed = false,
  onToggleCollapse,
  mobileOpen = false,
  onMobileClose,
}: SidebarProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const pendingReviewCount = usePendingReviewStore((s) => s.count) ?? 0
  const purchasingReviewCount = usePurchasingNavStore((s) => s.count) ?? 0
  const hasPurchasingReview = purchasingReviewCount > 0
  const purchasingReviewLabel =
    purchasingReviewCount === 1
      ? '1 purchasing item awaiting review'
      : `${purchasingReviewCount} purchasing items awaiting review`
  const hasPendingReview = pendingReviewCount > 0
  const pendingReviewLabel =
    pendingReviewCount === 1
      ? '1 document awaiting review'
      : `${pendingReviewCount} documents awaiting review`
  const ledgerErrorCount = useWarehouseProblemStore((s) => s.ledgerErrorCount)
  const uploadsErrorCount = useWarehouseProblemStore((s) => s.uploadsErrorCount)
  const warehouseErrorCount = ledgerErrorCount + uploadsErrorCount
  const hasWarehouseErrors = warehouseErrorCount > 0
  const warehouseErrorLabelParts: string[] = []
  if (ledgerErrorCount > 0) {
    warehouseErrorLabelParts.push(
      ledgerErrorCount === 1
        ? '1 ledger row with errors'
        : `${ledgerErrorCount} ledger rows with errors`
    )
  }
  if (uploadsErrorCount > 0) {
    warehouseErrorLabelParts.push(
      uploadsErrorCount === 1 ? '1 uploads error' : `${uploadsErrorCount} uploads errors`
    )
  }
  const warehouseErrorLabel = warehouseErrorLabelParts.join('; ')
  const fixedAssetsIncompleteCount = useFixedAssetsNavStore((s) => s.allocateIncompleteCount)
  const hasFixedAssetsErrors = fixedAssetsIncompleteCount > 0
  const fixedAssetsErrorLabel =
    fixedAssetsIncompleteCount === 1
      ? '1 incomplete Fixed Assets month'
      : `${fixedAssetsIncompleteCount} incomplete Fixed Assets months`
  const preferredEquityCount = useMonthCloseNavStore((s) => s.preferredEquityCount)
  const bankReconCount = useMonthCloseNavStore((s) => s.bankReconCount)
  const incomeAllocationCount = useMonthCloseNavStore((s) => s.incomeAllocationCount)
  const benefitsCount = useMonthCloseNavStore((s) => s.benefitsCount)
  const preferredEquityLabel =
    preferredEquityCount === 1
      ? '1 incomplete Preferred Equity month'
      : `${preferredEquityCount} incomplete Preferred Equity months`
  const bankReconLabel =
    bankReconCount === 1
      ? '1 incomplete Bank Recon month'
      : `${bankReconCount} incomplete Bank Recon months`
  const incomeAllocationLabel =
    incomeAllocationCount === 1
      ? '1 incomplete Income allocation month'
      : `${incomeAllocationCount} incomplete Income allocation months`
  const benefitsLabel =
    benefitsCount === 1
      ? '1 incomplete Benefits month'
      : `${benefitsCount} incomplete Benefits months`
  const allocateIncompleteCount = useCloverNavStore((s) => s.allocateIncompleteCount)
  const giftCardIncompleteCount = useCloverNavStore((s) => s.giftCardIncompleteCount)
  const cloverAlertCount = cloverSidebarAlertCount(
    allocateIncompleteCount,
    giftCardIncompleteCount,
  )
  const cloverAlertLabel = cloverSidebarAlertLabel(
    allocateIncompleteCount,
    giftCardIncompleteCount,
  )

  const isAppSettingsActive = location.pathname === '/settings'
  const showSettings = canView(user, 'settings')

  const visiblePayables = filterVisibleNavItems(payablesNavItems, user)
  const visibleBooks = filterVisibleNavItems(booksNavItems, user)
  const visibleMembers = filterVisibleNavItems(membersNavItems, user)
  const visibleFinancials = filterVisibleNavItems(financialsNavItems, user)

  const getItemAlert = (item: NavItemConfig): NavItemAlert | null => {
    if (item.pageKey === 'invoices' && hasPendingReview) {
      return { count: pendingReviewCount, label: pendingReviewLabel }
    }
    if (item.pageKey === 'warehouse' && hasWarehouseErrors) {
      return { count: warehouseErrorCount, label: warehouseErrorLabel }
    }
    if (item.pageKey === 'fixed-assets' && hasFixedAssetsErrors) {
      return { count: fixedAssetsIncompleteCount, label: fixedAssetsErrorLabel }
    }
    if (item.pageKey === 'preferred-equity' && preferredEquityCount > 0) {
      return {
        count: preferredEquityCount,
        label: preferredEquityLabel,
      }
    }
    if (item.pageKey === 'benefits' && benefitsCount > 0) {
      return {
        count: benefitsCount,
        label: benefitsLabel,
      }
    }
    if (item.pageKey === 'bank-recon' && bankReconCount > 0) {
      return { count: bankReconCount, label: bankReconLabel }
    }
    if (item.pageKey === 'distributions' && incomeAllocationCount > 0) {
      return {
        count: incomeAllocationCount,
        label: incomeAllocationLabel,
        tone: 'warning',
      }
    }
    if (item.pageKey === 'clover-reports' && cloverAlertCount > 0) {
      return { count: cloverAlertCount, label: cloverAlertLabel }
    }
    if (item.pageKey === 'purchasing' && hasPurchasingReview) {
      return {
        count: purchasingReviewCount,
        label: purchasingReviewLabel,
        tone: 'warning',
      }
    }
    return null
  }

  const userInitial =
    user?.name?.trim().charAt(0).toUpperCase() ||
    user?.email?.trim().charAt(0).toUpperCase() ||
    'U'

  const handleClose = () => {
    onMobileClose?.()
  }

  const handleLogout = () => {
    void logout()
  }

  return (
    <NavSidebar
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      mobileOpen={mobileOpen}
      onMobileClose={handleClose}
      brandAccent="Book"
      brandRest="Keeper"
      footer={
        <>
          <div className={`border-t border-gray-200 ${collapsed ? 'lg:mb-2 mb-3' : 'mb-3'}`} />

          {showSettings ? (
            <NavItem
              icon={<Cog6ToothIcon className="h-5 w-5" />}
              label="Settings"
              active={isAppSettingsActive}
              onClick={() => {
                navigate('/settings')
                handleClose()
              }}
            />
          ) : null}

          {showSettings ? (
            <div className={`border-t border-gray-200 ${collapsed ? 'lg:my-2 my-3' : 'my-3'}`} />
          ) : null}

          {user ? (
            <div
              className={`nav-item flex items-center mb-2 ${
                collapsed ? 'lg:justify-center lg:px-2 px-4' : 'px-4'
              }`}
              title={collapsed ? user.email : undefined}
            >
              <div className="nav-item__icon shrink-0 rounded-full bg-gray-200 flex items-center justify-center">
                <span className="text-[10px] font-medium text-gray-600">{userInitial}</span>
              </div>
              <span
                className={`truncate font-medium text-gray-700 ${
                  collapsed ? 'lg:hidden' : ''
                }`}
              >
                {user.email}
              </span>
            </div>
          ) : null}

          <button
            type="button"
            onClick={handleLogout}
            className={`nav-item flex w-full items-center rounded-lg text-red-600 transition-colors hover:bg-red-50 ${
              collapsed ? 'lg:justify-center lg:px-2 px-4' : 'px-4'
            }`}
            title={collapsed ? 'Logout' : undefined}
          >
            <svg
              className="nav-item__icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            <span className={`whitespace-nowrap ${collapsed ? 'lg:hidden' : ''}`}>Logout</span>
          </button>
        </>
      }
    >
      <SidebarNavSection
        label="Payables"
        items={visiblePayables}
        onClose={handleClose}
        getItemAlert={getItemAlert}
      />
      <SidebarNavSection
        label="Books"
        items={visibleBooks}
        onClose={handleClose}
        getItemAlert={getItemAlert}
      />
      <SidebarNavSection
        label="Members"
        items={visibleMembers}
        onClose={handleClose}
        getItemAlert={getItemAlert}
      />
      <SidebarNavSection
        label="Financials"
        items={visibleFinancials}
        onClose={handleClose}
        getItemAlert={getItemAlert}
      />
    </NavSidebar>
  )
}
